import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal'; // Correct import for react-bootstrap
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { getProfile, updateProfile } from '../../api/ProfileAPI';
import { handleApiError } from '../../api/risapi';
import AppBreadCrumb from '../common/AppBreadCrumb';
import AppToaster from '../AppToaster';
import { getAvatar } from '../../commonfunctions';

const ProfilePage = () => {
    // State for the modal's visibility
    const [showCropModal, setShowCropModal] = useState(false);
    
    // The modalRef and bsModal state are no longer needed
    const [user, setUser] = useState({ name: '', phone_number: '', date_of_birth: '', blood_group: '' });
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [croppedImageFile, setCroppedImageFile] = useState(null);
    const imgRef = useRef(null);
    const [profilePicPreview, setProfilePicPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);

    // This useEffect is now only for fetching data
    useEffect(() => {
        getProfile((response) => {
            setIsLoading(false);
            if (response.user) {
                setUser(response.user);
                setProfilePicPreview(response.user.profile_pic_url);
            }
        });
    }, []);

    // The useEffect for initializing the modal instance has been removed

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setCrop(undefined);
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImgSrc(reader.result.toString() || '');
                setShowCropModal(true); // Simplified call to show the modal
            });
            reader.readAsDataURL(e.target.files[0]);
            e.target.value = null;
        }
    };

    const handleCrop = () => {
        if (!completedCrop || !imgRef.current) {
            return;
        }
        // ... (canvas logic remains the same)
        const canvas = document.createElement('canvas');
        const image = imgRef.current;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = completedCrop.width * scaleX;
        canvas.height = completedCrop.height * scaleY;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, completedCrop.x * scaleX, completedCrop.y * scaleY, completedCrop.width * scaleX, completedCrop.height * scaleY, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
            if (blob) {
                const croppedFile = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
                setCroppedImageFile(croppedFile);
                setProfilePicPreview(URL.createObjectURL(blob));
                setShowCropModal(false); // Simplified call to hide the modal
            }
        }, 'image/jpeg');
    };
    
    // ... (onImageLoad, handleSubmit, and other logic remains the same)
    function onImageLoad(e) {
        const { width, height } = e.currentTarget;
        const crop = centerCrop(
            makeAspectCrop({ unit: '%', width: 90 }, 1, width, height),
            width,
            height
        );
        setCrop(crop);
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        const formData = {};
        formData.name = user.name ;
        formData.date_of_birth = user.date_of_birth || '' ;
        formData.blood_group = user.blood_group || '' ;
        if (password) {
            formData.password = password ;
            formData.password_confirmation = passwordConfirmation ;
        }
        if (croppedImageFile) {
            formData.profile_pic = croppedImageFile;
        }
        updateProfile(formData, (response) => {
            setIsLoading(false);
            if (response.user) {
                Swal.fire('Success!', 'Your profile has been updated.', 'success');
                setUser(response.user);
                if (response.user.profile_pic_url) {
                    setProfilePicPreview(response.user.profile_pic_url);
                }
                setErrors({});
                setPassword('');
                setPasswordConfirmation('');
            } else {
                handleApiError(response, setErrors, setToaster, setShowToaster);
            }
        });
    };

    if (isLoading && !user.id) {
        return <div className="text-center p-5"><div className="spinner-border"></div></div>;
    }
    
    return (
        <main>
            {/* ... (The main page form JSX remains the same) ... */}
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title="My Profile" />
                <div className="card">
                    <div className="card-header">
                        <h5>Edit Your Profile Information</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-4 text-center">
                                    <img 
                                        src={profilePicPreview || getAvatar(user.profile_pic_path, user?.personalInformation?.gender)} 
                                        alt="Profile Preview" 
                                        className="img-thumbnail rounded-circle mb-3"
                                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                    />
                                    <input type="file" accept="image/*" className={`form-control ${errors.profile_pic ? 'is-invalid' : ''}`} onChange={handleFileChange} />
                                    {errors.profile_pic && <div className="invalid-feedback">{errors.profile_pic[0]}</div>}
                                </div>
                                <div className="col-md-8">
                                    <div className="mb-3">
                                        <label className="form-label">Full Name</label>
                                        <input type="text" name="name" className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={user.name} onChange={(e) => setUser({ ...user, name: e.target.value })} />
                                        {errors.name && <div className="invalid-feedback">{errors.name[0]}</div>}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Phone Number</label>
                                        <input type="text" className="form-control" value={user.phone_number} disabled />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Date of Birth</label>
                                            <input type="date" name="date_of_birth" className={`form-control ${errors.date_of_birth ? 'is-invalid' : ''}`} value={user.date_of_birth || ''} onChange={(e) => setUser({ ...user, date_of_birth: e.target.value })} />
                                            {errors.date_of_birth && <div className="invalid-feedback">{errors.date_of_birth[0]}</div>}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Blood Group</label>
                                            <select name="blood_group" className={`form-select ${errors.blood_group ? 'is-invalid' : ''}`} value={user.blood_group || ''} onChange={(e) => setUser({ ...user, blood_group: e.target.value })}>
                                                <option value="">Select...</option>
                                                <option value="A+">A+</option>
                                                <option value="A-">A-</option>
                                                <option value="B+">B+</option>
                                                <option value="B-">B-</option>
                                                <option value="AB+">AB+</option>
                                                <option value="AB-">AB-</option>
                                                <option value="O+">O+</option>
                                                <option value="O-">O-</option>
                                            </select>
                                            {errors.blood_group && <div className="invalid-feedback">{errors.blood_group[0]}</div>}
                                        </div>
                                    </div>
                                    <hr />
                                    <h5>Update Password</h5>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">New Password</label>
                                            <input type="password" className={`form-control ${errors.password ? 'is-invalid' : ''}`} value={password} onChange={(e) => setPassword(e.target.value)} />
                                            {errors.password && <div className="invalid-feedback">{errors.password[0]}</div>}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Confirm New Password</label>
                                            <input type="password" className="form-control" value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-end mt-3">
                                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                    {isLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* --- Updated Modal using react-bootstrap --- */}
            <Modal show={showCropModal} onHide={() => setShowCropModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Crop Your Image</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {imgSrc && (
                        <ReactCrop
                            crop={crop}
                            onChange={c => setCrop(c)}
                            onComplete={c => setCompletedCrop(c)}
                            aspect={1}
                        >
                            <img ref={imgRef} alt="Crop me" src={imgSrc} onLoad={onImageLoad} />
                        </ReactCrop>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCropModal(false)}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleCrop}>
                        Crop and Save
                    </button>
                </Modal.Footer>
            </Modal>
        </main>
    );
};

export default ProfilePage;